// Client-side helper for fetching broker metadata directly from
// Azure Blob Storage (trading-data-v2/raw/brokermetadata.parquet).

import { parquetRead, parquetMetadata } from 'hyparquet';
import { compressors } from 'hyparquet-compressors';
import { api } from '../services/api';
import { blobCacheKey } from './cacheKey';

const BROKER_METADATA_PATH = 'raw/brokermetadata.parquet';
const CACHE_NAME = 'broker-metadata-parquet-cache';

export interface BrokerMetadataRow {
  code: string;
  name?: string | undefined;
  type?: string | undefined;
}

function localDateKey(d = new Date()): string {
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

function asyncBufferFromArrayBuffer(arrayBuffer: ArrayBuffer) {
  return {
    byteLength: arrayBuffer.byteLength,
    slice: (start: number, end?: number): Promise<ArrayBuffer> =>
      Promise.resolve(arrayBuffer.slice(start, end)),
  };
}

let cachedRows: BrokerMetadataRow[] | null = null;
let cachedDate: string | null = null;
let loadPromise: Promise<BrokerMetadataRow[]> | null = null;

async function parseArrayBuffer(arrayBuffer: ArrayBuffer): Promise<BrokerMetadataRow[]> {
  const metadata = parquetMetadata(arrayBuffer as any);
  const columnNames = (metadata.schema || []).slice(1).map((f: any) => f.name.toLowerCase());
  const asyncBuf = asyncBufferFromArrayBuffer(arrayBuffer);
  const rawRows: any[][] = [];
  await parquetRead({
    file: asyncBuf,
    compressors,
    onComplete: (data: any[][]) => {
      for (const row of data) {
        if (row) {
          rawRows.push(row);
        }
      }
    },
  });

  return rawRows.map(rowArr => {
    const obj: Record<string, any> = {};
    columnNames.forEach((col, i) => {
      let v = rowArr[i];
      if (typeof v === 'bigint') v = Number(v);
      obj[col] = v;
    });

    // Try common column names for broker code
    const code = String(obj['broker'] ?? obj['code'] ?? obj['broker_code'] ?? obj['id'] ?? '').toUpperCase();
    
    const row: BrokerMetadataRow = {
      code,
      name: obj['name'] ? String(obj['name']) : undefined,
      type: obj['type'] ? String(obj['type']) : undefined,
    };
    return row;
  }).filter(r => r.code);
}

const FALLBACK_CACHE_KEY = `${BROKER_METADATA_PATH}::latest`;

async function fetchAndParse(): Promise<BrokerMetadataRow[]> {
  const cache = await caches.open(CACHE_NAME);

  const todayKey = localDateKey();
  const todayCacheKey = `${BROKER_METADATA_PATH}::${todayKey}`;
  const todayHit = await cache.match(blobCacheKey(todayCacheKey));
  if (todayHit) {
    return parseArrayBuffer(await todayHit.arrayBuffer());
  }

  let arrayBuffer: ArrayBuffer | null = null;
  try {
    const tokenRes = await api.getBrokerSummarySasToken();
    if (!tokenRes.success || !tokenRes.data) throw new Error(tokenRes.error || 'Failed to get SAS token');
    const { containerUrl, sasToken } = tokenRes.data;
    const isDev = (import.meta as any).env?.DEV || false;
    const blobUrl = isDev
      ? `/azure-blob-proxy/trading-data-v2/${BROKER_METADATA_PATH}?${sasToken}`
      : `${containerUrl}/${BROKER_METADATA_PATH}?${sasToken}`;

    const response = await fetch(blobUrl);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    arrayBuffer = await response.arrayBuffer();

    const buf = arrayBuffer.slice(0);
    await cache.put(blobCacheKey(todayCacheKey), new Response(buf, { headers: { 'Content-Type': 'application/octet-stream' } }));
    await cache.put(blobCacheKey(FALLBACK_CACHE_KEY), new Response(arrayBuffer.slice(0), { headers: { 'Content-Type': 'application/octet-stream' } }));
  } catch (fetchErr) {
    console.warn('[brokerMetadata] blob fetch failed, trying last cached data:', fetchErr);
  }

  if (!arrayBuffer) {
    const fallbackHit = await cache.match(blobCacheKey(FALLBACK_CACHE_KEY));
    if (fallbackHit) {
      console.warn('[brokerMetadata] using cached fallback');
      return parseArrayBuffer(await fallbackHit.arrayBuffer());
    }
    throw new Error('brokermetadata.parquet unavailable and no cached fallback found');
  }

  return parseArrayBuffer(arrayBuffer);
}

export async function fetchBrokerMetadata(): Promise<BrokerMetadataRow[]> {
  if (cachedRows) {
    return cachedRows;
  }
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = (async () => {
    try {
      const rows = await fetchAndParse();
      cachedRows = rows;
      return rows;
    } finally {
      loadPromise = null;
    }
  })();

  return loadPromise;
}

export async function fetchBrokerList(): Promise<string[]> {
  const rows = await fetchBrokerMetadata();
  return Array.from(new Set(rows.map(r => r.code).filter(Boolean))).sort();
}
